// "×2" on a lit square: how many foundries are splitting the bill (PLAN-UI
// §28.4, screen-mock `.sq.can .co`).
//
// **The mark is on the board because the fact is about a square.** Since U-R a
// forge is paid for by every foundry of yours touching the landing square, so
// two rooks each holding half a pawn can between them mint one — and the only
// place that is legible is the square itself. A count in the hotbar would be
// answering "how many", which nobody asked, instead of "which squares".
//
// Drawn only while a forge is armed, and only where the number is more than
// one: `hotbar.ts` populates `shared` with the split squares alone, because a
// "×1" on every ordinary lit square is a decoration on the whole board.

import type { RefObject } from "react";

import type { BoardCanvasHandle } from "./BoardCanvas";
import { BoardAnchored, styled } from "./kit";

/**
 * The pill. Cream on a wash of the page's own ground rather than a fill of its
 * own: it sits over a lit square and anything opaque enough to read against
 * the board would also hide the highlight it is annotating.
 *
 * **It explains itself with nothing.** `BoardAnchored` takes no pointer unless
 * a caller asks for it, so a hover here can never happen — the mark carried a
 * native `title` for a while and it could never have shown. What "×2" means is
 * said where the player is looking when they arm a rank: the armed hotbar
 * tile's own card carries the coalition-priced bill.
 */
const Mark = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontWeight: 700,
  fontSize: 9,
  color: theme.kit.color.cream,
  background: `color-mix(in srgb, ${theme.kit.color.ground} 60%, transparent)`,
  borderRadius: theme.kit.radius.sm,
  padding: "0 4px",
  whiteSpace: "nowrap"
}));

export function CoFundMarks({
  board,
  shared
}: {
  board: RefObject<BoardCanvasHandle | null>;
  /** Square → how many foundries pay. Absent when nothing is armed. */
  shared: ReadonlyMap<number, number> | undefined;
}) {
  if (!shared || shared.size === 0) return null;

  return (
    <>
      {[...shared].map(([square, funders]) => (
        <BoardAnchored
          key={square}
          board={board}
          square={square}
          // The port projects one corner — the square's bottom-right in screen
          // space, which follows the yaw — so the mock's top-right is reached
          // by hanging the pill *above* it rather than by asking the renderer
          // for a second projection. A lit square is bare ground, so there is
          // no badge here to collide with.
          mode="corner"
          align="above"
          offset={{ x: -6, y: -4 }}
          data-testid={`cofund-${square}`}
        >
          <Mark>×{funders}</Mark>
        </BoardAnchored>
      ))}
    </>
  );
}
